import type {
  AgencyMasterGroupAssignUserInput,
  AgencyMasterGroupCreateInput,
  AgencyMasterGroupDto,
  AgencyMasterGroupGetAllParams,
  AgencyMasterGroupUpdateInput,
  AgencyMasterGroupUserDto,
} from "@models/agencyMasterGroup/agencyMasterGroupModel"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import agencyMasterGroupService from "@services/agencyMasterGroup/agencyMasterGroupService"
import { action, makeAutoObservable, observable } from "mobx"

class AgencyMasterGroupStore {
  @observable isLoading!: boolean
  @observable tableData!: PagedResultDto<AgencyMasterGroupDto>
  @observable groupDetail!: AgencyMasterGroupDto | null
  @observable groupUsers!: AgencyMasterGroupUserDto[]
  @observable managedUsers!: AgencyMasterGroupUserDto[]

  constructor() {
    makeAutoObservable(this)
    this.tableData = { items: [], totalCount: 0 }
    this.groupDetail = null
    this.groupUsers = []
    this.managedUsers = []
  }

  @action
  resetGroupContext() {
    this.groupDetail = null
    this.groupUsers = []
    this.managedUsers = []
  }

  @action
  async getAllPaged(params: AgencyMasterGroupGetAllParams) {
    this.isLoading = true
    this.tableData = await agencyMasterGroupService
      .getAll(params)
      .finally(() => {
        this.isLoading = false
      })
  }

  @action
  async get(id: number) {
    this.isLoading = true
    this.groupDetail = await agencyMasterGroupService
      .get(id)
      .finally(() => {
        this.isLoading = false
      })
    return this.groupDetail
  }

  @action
  async create(body: AgencyMasterGroupCreateInput) {
    this.isLoading = true
    const created = await agencyMasterGroupService
      .create(body)
      .finally(() => {
        this.isLoading = false
      })
    return created
  }

  @action
  async update(body: AgencyMasterGroupUpdateInput) {
    this.isLoading = true
    const updated = await agencyMasterGroupService
      .update(body)
      .finally(() => {
        this.isLoading = false
      })
    return updated
  }

  @action
  async delete(id: number) {
    this.isLoading = true
    await agencyMasterGroupService.delete(id).finally(() => {
      this.isLoading = false
    })
  }

  @action
  async assignUser(body: AgencyMasterGroupAssignUserInput) {
    this.isLoading = true
    await agencyMasterGroupService.assignUser(body).finally(() => {
      this.isLoading = false
    })
  }

  @action
  async assignManyUsers(assignments: AgencyMasterGroupAssignUserInput[]) {
    if (!assignments.length) return
    this.isLoading = true
    await agencyMasterGroupService
      .assignManyUsers(assignments)
      .finally(() => {
        this.isLoading = false
      })
  }

  @action
  async removeUser(groupId: number, userId: number) {
    this.isLoading = true
    await agencyMasterGroupService.removeUser(groupId, userId).finally(() => {
      this.isLoading = false
    })
  }

  @action
  async fetchGroupUsers(groupId: number) {
    this.isLoading = true
    this.groupUsers = await agencyMasterGroupService
      .getGroupUsers(groupId)
      .finally(() => {
        this.isLoading = false
      })
    return this.groupUsers
  }

  @action
  async fetchManagedUsers() {
    this.isLoading = true
    this.managedUsers = await agencyMasterGroupService
      .getManagedUsers()
      .finally(() => {
        this.isLoading = false
      })
    return this.managedUsers
  }
}

export default AgencyMasterGroupStore
